import { workRunIdSchema } from "./work.js";
import { PERMISSION_KEY_PATTERN } from "./permission-key.js";
import { OIDC_CLAIM_NAME_PATTERN, oidcIssuerSchema } from "./federated-token.js";
import { z } from "zod";
//#region src/oidc-app.ts
/**
* Platform OIDC for apps: the wire contract (feature 2026-10-11-app-oidc, WS-1).
*
* Two directions, both owned by the platform, so an app writes no OIDC code (D1):
*
* 1. Inbound. A fixture declares `capabilities.oidc.accept[]` in its `fixture.json`: the routes
*    an outside machine caller may reach, the one app permission each needs, and the outside claim
*    values each requires. An org admin enables the `oidc` capability for the app (D8) and binds a
*    trust policy to the app with a subset of those permissions (D9). The federated exchange then
*    mints a token that carries the outside identity as `oidc` (`OidcCallerIdentity` in
*    `federated-token.ts`, D11), and `@AcceptOidc` in the SDK checks it (D12).
* 2. Outbound. A fixture declares `capabilities.oidc.issue.audiences`. The app asks
*    `POST /internal/v1/oidc-tokens` ({@link OIDC_TOKENS_PATH}) with its service bearer for an OIDC
*    app token of one of its running runs (D13). core-api signs it with the org key, issuer
*    `https://<org host>/oidc`, scope {@link OIDC_APP_TOKEN_SCOPE}, 300 s (D14). A subject or claims
*    the app chooses need the kernel right `oidc.claims` (D5, D15).
*
* The JSON Schema twin of the fixture block is `$defs/oidcCapability` in
* `cloud/platform-sdk/schemas/fixture.schema.json`; a parity test pins the two. Every schema here
* is `.strict()` and carries no `.transform()`, so `ZionStrictValidationPipe` refuses an unknown key
* and `createOpenApiDocument()` can render every schema. Pure: no I/O.
*/
/**
* The one capability id an `oidc` declaration row and an `oidc` enablement row carry, per fixture
* (D8). `release.app_capability_declarations` and `release.org_app_capability_enablements` key a
* row by `(fixture, kind, id)`, and an `oidc` row has no id of its own, so every writer and reader
* uses this literal. It matches the capability id grammar `^[a-z][a-z0-9-]*$`.
*/
const OIDC_CAPABILITY_ID = "oidc";
/** The most `accept[]` entries one fixture declares. */
const OIDC_ACCEPT_MAX_ENTRIES = 32;
/** The most `claims` one `accept[]` entry requires. */
const OIDC_ACCEPT_MAX_CLAIMS = 8;
/** The most `issue.audiences` one fixture declares. */
const OIDC_ISSUE_MAX_AUDIENCES = 8;
/** The longest audience an OIDC app token may name, in characters. */
const OIDC_AUDIENCE_MAX_LENGTH = 256;
/**
* The longest `accept[].path`, in characters: the bound `route-inventory.schema.json` puts on a
* route `path`, so every accept path can name an inventoried route.
*/
const OIDC_ACCEPT_PATH_MAX_LENGTH = 512;
/** The HTTP methods an `accept[]` entry may name: the `expose.internal` method set. */
const OIDC_HTTP_METHODS = [
	"GET",
	"POST",
	"PUT",
	"PATCH",
	"DELETE",
	"HEAD",
	"OPTIONS"
];
/**
* The grammar of an `accept[].path`: the `services[].apiPaths` item pattern, byte for byte. A test
* pins it equal to the JSON Schema pattern. The pattern is not quoted in this comment, because it
* holds the two characters that close a block comment.
*/
const OIDC_ACCEPT_PATH_PATTERN = /^\/v1\/[A-Za-z0-9_.~-]+(\/[A-Za-z0-9_.~-]+)*\/?$/;
/**
* The form of an OIDC app token audience: an `https` URL of at most
* {@link OIDC_AUDIENCE_MAX_LENGTH} characters, under the grammar of `oidcIssuerSchema` (a lower-case
* DNS host, an optional port, a path of non-empty segments, no user info, query or fragment, a
* spelling the URL parser reads back unchanged). `http://`, an IP-literal host and every rewritten
* spelling are refused (overview security table: audiences `https` only).
*/
const oidcAudienceSchema = oidcIssuerSchema.max(256);
/**
* One required outside claim value: 1 to {@link OIDC_CLAIM_VALUE_MAX_LENGTH} characters. A string
* only: `forwardOidcClaims` carries a number or a boolean claim as its string form, so the author
* writes `'true'` or `'1'` for such a claim.
*/
const requiredClaimValueSchema = z.string().min(1).max(256);
/**
* One {@link FixtureOidcAcceptEntry}. Refused: a path outside {@link OIDC_ACCEPT_PATH_PATTERN} or
* over {@link OIDC_ACCEPT_PATH_MAX_LENGTH} characters; an empty, duplicate or unknown method; a
* permission that is not a permission key; more than {@link OIDC_ACCEPT_MAX_CLAIMS} claims; a
* claim name outside `OIDC_CLAIM_NAME_PATTERN`; an empty or over-long claim value; any other key.
* The reserved claim names pass here and fail the `oidc-accept-claim-reserved` rule.
*/
const fixtureOidcAcceptEntrySchema = z.object({
	path: z.string().max(512).regex(OIDC_ACCEPT_PATH_PATTERN),
	methods: z.array(z.enum(OIDC_HTTP_METHODS)).min(1).refine((methods) => new Set(methods).size === methods.length, { message: "methods are unique" }).optional(),
	permission: z.string().regex(PERMISSION_KEY_PATTERN),
	claims: z.record(z.string().regex(OIDC_CLAIM_NAME_PATTERN), requiredClaimValueSchema).refine((claims) => Object.keys(claims).length <= 8, { message: `claims: at most 8 entries` }).optional()
}).strict();
/** One {@link FixtureOidcIssue}: 1 to {@link OIDC_ISSUE_MAX_AUDIENCES} unique audiences. */
const fixtureOidcIssueSchema = z.object({ audiences: z.array(oidcAudienceSchema).min(1).max(8).refine((audiences) => new Set(audiences).size === audiences.length, { message: "audiences are unique" }) }).strict();
/**
* One {@link FixtureOidcCapability}: `accept` of 1 to {@link OIDC_ACCEPT_MAX_ENTRIES} entries,
* `issue`, or both. `{}` is refused, because it declares nothing an org admin could enable.
*/
const fixtureOidcCapabilitySchema = z.object({
	accept: z.array(fixtureOidcAcceptEntrySchema).min(1).max(32).optional(),
	issue: fixtureOidcIssueSchema.optional()
}).strict().refine((block) => block.accept !== void 0 || block.issue !== void 0, { message: "capabilities.oidc declares accept, issue or both" });
/**
* The `scope` of an OIDC app token (D14). It is not `data_plane`, so `verifyDpJwt`, core-api's
* `JwtAuthGuard` and the SDK `RemoteJwksJwtValidator` refuse the token as a caller credential: the
* `dp_assets` precedent.
*/
const OIDC_APP_TOKEN_SCOPE = "oidc_app";
/** The lifetime of an OIDC app token, `exp - iat`, in seconds (D14). */
const OIDC_APP_TOKEN_TTL_SECONDS = 300;
/** The core-api route an app calls with its service bearer for an OIDC app token (D13). */
const OIDC_TOKENS_PATH = "/internal/v1/oidc-tokens";
/** The path the OIDC app token issuer adds to the org issuer (D14). */
const OIDC_ISSUER_PATH = "/oidc";
/** The public discovery document of the OIDC app token issuer, on the org host. */
const OIDC_DISCOVERY_PATH = "/oidc/.well-known/openid-configuration";
/** The public JWKS of the OIDC app token issuer, on the org host. */
const OIDC_JWKS_PATH = "/oidc/jwks";
/**
* True when `orgIssuer` is an org issuer: it passes `oidcIssuerSchema` and names an origin, a
* URL whose path is `/` (`https://<org host>` or `https://<org host>/`, with an optional port).
* The org issuer is the origin of the org host (core-api `federatedAssertionAudiences`), so an
* issuer with a path names no org. Never throws: `oidcIssuerSchema` admits only a string the URL
* parser reads. Pure.
*/
function isOrgIssuer(orgIssuer) {
	return oidcIssuerSchema.safeParse(orgIssuer).success && new URL(orgIssuer).pathname === "/";
}
/**
* The OIDC app token issuer of an org: `orgIssuer` with one trailing `/` removed, then
* {@link OIDC_ISSUER_PATH}. `https://acme.zion.witness.ai` gives `https://acme.zion.witness.ai/oidc`.
*
* @param orgIssuer the org issuer, `https://<org host>` with an optional port and an optional
*   trailing `/`, as `JwksService` holds it.
* @returns the issuer string the token `iss` and the discovery `issuer` carry.
* @throws TypeError when `orgIssuer` fails `oidcIssuerSchema` or carries a path, so no `http`,
*   rewritten or nested issuer is ever minted. Pure otherwise.
*/
function oidcAppIssuer(orgIssuer) {
	if (!isOrgIssuer(orgIssuer)) throw new TypeError("oidcAppIssuer: the org issuer is not a canonical https origin");
	return `${orgIssuer.endsWith("/") ? orgIssuer.slice(0, -1) : orgIssuer}${OIDC_ISSUER_PATH}`;
}
/**
* The claims core-api sets from the run row and the caller's service bearer, never from the
* request (D13): the org, the app slug, the fixture and the run. The platform vouches for them.
*/
const OIDC_APP_TOKEN_PLATFORM_CLAIMS = Object.freeze([
	"zion_org_id",
	"zion_app_slug",
	"zion_fixture_id",
	"zion_run_id"
]);
/** The prefix no app-asserted claim may carry. Every platform claim carries it. */
const OIDC_APP_PLATFORM_CLAIM_PREFIX = "zion_";
/**
* The claim names an app may never assert in any letter case, besides any name with the
* {@link OIDC_APP_PLATFORM_CLAIM_PREFIX}. Every entry is lower case, and
* {@link isAssertableOidcAppClaimName} compares the lower-case form of a name with them:
*
* 1. the registered JWT claims `iss`, `sub`, `aud`, `exp`, `nbf`, `iat`, `jti`;
* 2. `scope`, the token class;
* 3. the OIDC Core claims `nonce`, `auth_time`, `acr`, `amr`, `at_hash`, `c_hash`, `azp`, `sid`,
*    and the RFC 7800 `cnf`;
* 4. `constructor` and `prototype`.
*
* Frozen.
*/
const OIDC_APP_TOKEN_RESERVED_CLAIMS = Object.freeze([
	"iss",
	"sub",
	"aud",
	"exp",
	"nbf",
	"iat",
	"jti",
	"scope",
	"nonce",
	"auth_time",
	"acr",
	"amr",
	"at_hash",
	"c_hash",
	"azp",
	"sid",
	"cnf",
	"constructor",
	"prototype"
]);
const OIDC_APP_RESERVED = new Set(OIDC_APP_TOKEN_RESERVED_CLAIMS);
/** The most claims one OIDC app token request asserts. */
const OIDC_APP_CLAIMS_MAX = 16;
/** The longest `subject` an app asserts, in characters. */
const OIDC_APP_SUBJECT_MAX_LENGTH = 256;
/**
* True when an app may assert a claim named `name` (D15): it matches `OIDC_CLAIM_NAME_PATTERN`, its
* lower-case form is not in {@link OIDC_APP_TOKEN_RESERVED_CLAIMS}, and its lower-case form does not
* start with {@link OIDC_APP_PLATFORM_CLAIM_PREFIX}. Never throws. Pure.
*
* Both checks fold the letter case. A relying party that looks a claim up with no regard to case
* (.NET `ClaimsIdentity.FindFirst` compares claim types with `OrdinalIgnoreCase`) and takes the
* first match would read an app claim `Sub` or `ISS` in place of the platform `sub` or `iss`,
* because {@link buildOidcAppTokenPayload} writes the app claims first. So `Sub`, `ISS`, `Aud`,
* `Scope` and `Zion_org_id` are refused, as `sub`, `iss`, `aud`, `scope` and `zion_org_id` are.
*/
function isAssertableOidcAppClaimName(name) {
	if (typeof name !== "string" || !OIDC_CLAIM_NAME_PATTERN.test(name)) return false;
	const folded = name.toLowerCase();
	return !OIDC_APP_RESERVED.has(folded) && !folded.startsWith("zion_");
}
/**
* The `subject` an app asserts: 1 to {@link OIDC_APP_SUBJECT_MAX_LENGTH} printable ASCII characters
* other than `*` and `%` (the trust policy subject grammar, so a relying party can match the
* resulting `sub`), and not starting with `run:`, the form a platform `sub` takes after its
* `org:<orgSlug>:app:<appSlug>:` part.
*/
const oidcAppSubjectSchema = z.string().min(1).max(256).regex(/^[!-$&-)+-~]+$/, "subject: printable ASCII with no *, %, whitespace or control character").refine((subject) => !subject.startsWith("run:"), "subject: run: is the platform form");
/**
* The claims an app asserts: at most {@link OIDC_APP_CLAIMS_MAX} entries; each name passes
* {@link isAssertableOidcAppClaimName}; each value is a string of 1 to
* `OIDC_CLAIM_VALUE_MAX_LENGTH` characters.
*/
const oidcAppClaimsSchema = z.record(z.string().regex(OIDC_CLAIM_NAME_PATTERN).refine((name) => isAssertableOidcAppClaimName(name), "claim name: reserved"), z.string().min(1).max(256)).refine((claims) => Object.keys(claims).length <= 16, { message: `claims: at most 16 entries` });
/**
* The body of `POST /internal/v1/oidc-tokens` (D13). core-api (WS-7) binds it; sdk-node (WS-8)
* sends it.
*
* - `runId`: the app pool run, `workRunIdSchema`. core-api reads the run row and refuses unless its
*   app and fixture equal the caller's and its state is `running`.
* - `audience`: {@link oidcAudienceSchema}. core-api refuses unless it is in the fixture's
*   `issue.audiences` and {@link isOidcAppAudienceAllowed} holds for the org issuer. That predicate
*   refuses the org issuer and the org's own federated exchange URL, the two audiences the exchange
*   accepts, so an app can never exchange its own OIDC app token at its own org.
* - `subject`: optional {@link oidcAppSubjectSchema}. Needs `oidc.claims` granted and consented.
* - `claims`: optional {@link oidcAppClaimsSchema}. Needs `oidc.claims` granted and consented.
*
* `.strict()`: any other key is a 400.
*/
const oidcAppTokenRequestSchema = z.object({
	runId: workRunIdSchema,
	audience: oidcAudienceSchema,
	subject: oidcAppSubjectSchema.optional(),
	claims: oidcAppClaimsSchema.optional()
}).strict();
/** The longest OIDC app token the response carries, in characters. */
const OIDC_APP_TOKEN_MAX_LENGTH = 16384;
/**
* The 200 body of `POST /internal/v1/oidc-tokens`.
*
* - `token`: the OIDC app token, a compact JWS of at most {@link OIDC_APP_TOKEN_MAX_LENGTH}
*   characters.
* - `expiresIn`: whole seconds until it expires, 1 to {@link OIDC_APP_TOKEN_TTL_SECONDS}.
*
* `.strict()`, and there is no refresh member: the app asks again.
*/
const oidcAppTokenResponseSchema = z.object({
	token: z.string().max(OIDC_APP_TOKEN_MAX_LENGTH).regex(/^[A-Za-z0-9_-]+\.[A-Za-z0-9_-]+\.[A-Za-z0-9_-]+$/, "token: a compact JWS"),
	expiresIn: z.number().int().min(1).max(300)
}).strict();
/** The longest org slug {@link isOrgHostSlug} admits, in characters: the DNS name bound. */
const ORG_HOST_SLUG_MAX_LENGTH = 253;
/**
* The org host grammar: one or more DNS labels joined by `.`, each label 1 to 63 lower-case letters,
* digits and inner hyphens. It is the host grammar of `oidcIssuerSchema` (`DNS_HOST` in
* `federated-token.ts`), so it holds no `:`. Anchored at both ends; `.` separates the labels, so no
* two quantifiers compete for one character.
*/
const ORG_HOST_SLUG_PATTERN = /^[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?(?:\.[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?)*$/;
/**
* True when `orgSlug` can lead an org host: at most {@link ORG_HOST_SLUG_MAX_LENGTH} characters
* (checked before the pattern), then {@link ORG_HOST_SLUG_PATTERN}.
*
* core-api composes the org issuer as `https://<orgSlug>.<apex>` (`OrgSigningKeyService`), and
* {@link oidcAppIssuer} refuses an issuer whose host fails the DNS host grammar. So every org slug
* that has an OIDC app token issuer passes this predicate: `x` and `9` (one character) included,
* which `RESOURCE_SLUG_PATTERN` refuses. A slug with a `:`, an upper-case letter, a `_` or an empty
* label fails it, and fails the issuer first. Never throws. Pure.
*/
function isOrgHostSlug(orgSlug) {
	return orgSlug.length <= ORG_HOST_SLUG_MAX_LENGTH && ORG_HOST_SLUG_PATTERN.test(orgSlug);
}
/**
* The `sub` of an OIDC app token (D15, amended by ruling R-015). core-api (WS-7) calls it through
* {@link buildOidcAppTokenPayload}; the sdk-node fake (WS-8) builds the same form.
*
* - With no `subject`: `org:<orgSlug>:app:<appSlug>:run:<runId>`, the platform form.
* - With a `subject`: `org:<orgSlug>:app:<appSlug>:<subject>`. The `run:` refusal of
*   {@link oidcAppSubjectSchema} keeps it apart from the platform form.
*
* One issuer key signs the tokens of every org that a data plane holds, so the org slug leads the
* subject. Two orgs then give two different subjects for the same app, run and asserted subject,
* and a relying party that matches `iss` and `sub` refuses a token of another org.
*
* @param orgSlug the slug of the run's data-plane org, from `auth.organizations`. It must pass the
*   org host grammar: DNS labels of 1 to 63 lower-case letters, digits and inner hyphens, joined by
*   `.`, at most 253 characters. Every slug whose org issuer `https://<orgSlug>.<apex>` passes
*   {@link oidcAppIssuer} passes it, a one-character slug included. It holds no `:`, so the org part
*   of the subject has one reading.
* @param appSlug the caller's app slug, equal to the run row's app.
* @param runId the run id of the request.
* @param subject the asserted subject, present only when the app holds `oidc.claims`.
* @throws TypeError `oidcAppTokenSubject: the org slug fails the org host grammar` when `orgSlug`
*   fails that grammar. This check runs first.
* @throws ZodError when `subject` is present and fails {@link oidcAppSubjectSchema}. Pure otherwise.
*/
function oidcAppTokenSubject(orgSlug, appSlug, runId, subject) {
	if (!isOrgHostSlug(orgSlug)) throw new TypeError("oidcAppTokenSubject: the org slug fails the org host grammar");
	const prefix = `org:${orgSlug}:app:${appSlug}:`;
	if (subject === void 0) return `${prefix}run:${runId}`;
	return `${prefix}${oidcAppSubjectSchema.parse(subject)}`;
}
/**
* True when `sub` has the form {@link oidcAppTokenSubject} prints for the app `appSlug`: `org:`, an
* org slug that passes {@link isOrgHostSlug}, then `:app:<appSlug>:`. Never throws. Pure.
*/
function isOidcAppTokenSubjectOf(sub, appSlug) {
	if (!sub.startsWith("org:")) return false;
	const rest = sub.slice(4);
	const end = rest.indexOf(":");
	if (end === -1 || !isOrgHostSlug(rest.slice(0, end))) return false;
	return rest.slice(end).startsWith(`:app:${appSlug}:`);
}
/**
* The payload core-api (WS-7) signs for one OIDC app token. The app claims come first, in
* code-unit order of their names; the registered and platform claims follow, so no app claim can
* replace one even if a caller skipped the request schema.
*
* @returns a new {@link OidcAppTokenPayload}; `nbf` is `issuedAt` and `exp` is `issuedAt` plus
*   {@link OIDC_APP_TOKEN_TTL_SECONDS}.
* @throws TypeError when `issuer` is not {@link oidcAppIssuer} of an org issuer: an issuer that is
*   not canonical, has no {@link OIDC_ISSUER_PATH} suffix, ends in `/oidc/`, or nests the suffix
*   (`https://<org host>/oidc/oidc`). This check runs first.
* @throws TypeError when {@link isOidcAppAudienceAllowed} refuses `audience` for that org issuer:
*   a form {@link oidcAudienceSchema} refuses, the org issuer or the org's own federated exchange
*   URL. This check runs second, so no payload is built.
* @throws TypeError when `orgSlug` fails the org host grammar ({@link oidcAppTokenSubject}).
*   This check runs third.
* @throws ZodError when `subject` fails {@link oidcAppSubjectSchema} (fourth) or `claims` fails
*   {@link oidcAppClaimsSchema} (fifth): a reserved name, a `zion_` name, more than
*   {@link OIDC_APP_CLAIMS_MAX} entries or a bad value. Pure otherwise.
*/
function buildOidcAppTokenPayload(input) {
	const orgIssuer = orgIssuerOfOidcAppIssuer(input.issuer);
	if (orgIssuer === void 0) throw new TypeError("buildOidcAppTokenPayload: the issuer is not the OIDC app token issuer of an org");
	if (!isOidcAppAudienceAllowed(input.audience, orgIssuer)) throw new TypeError("buildOidcAppTokenPayload: the audience is not an allowed OIDC app token audience of the org");
	const sub = oidcAppTokenSubject(input.orgSlug, input.appSlug, input.runId, input.subject);
	const claims = input.claims === void 0 ? {} : oidcAppClaimsSchema.parse(input.claims);
	const appClaims = {};
	for (const name of Object.keys(claims).sort()) appClaims[name] = claims[name];
	return {
		...appClaims,
		iss: input.issuer,
		sub,
		aud: input.audience,
		scope: OIDC_APP_TOKEN_SCOPE,
		iat: input.issuedAt,
		nbf: input.issuedAt,
		exp: input.issuedAt + 300,
		jti: input.jti,
		zion_org_id: input.orgId,
		zion_app_slug: input.appSlug,
		zion_fixture_id: input.fixtureId,
		zion_run_id: input.runId
	};
}
/** True when `name` is one of the fixed members of {@link OidcAppTokenPayload}. */
const FIXED_PAYLOAD_MEMBERS = /* @__PURE__ */ new Set([
	"iss",
	"sub",
	"aud",
	"scope",
	"iat",
	"nbf",
	"exp",
	"jti",
	...OIDC_APP_TOKEN_PLATFORM_CLAIMS
]);
/**
* The zod form of {@link OidcAppTokenPayload}, for a reader of a verified token (a relying party
* test, the WS-7 contract test). It refuses:
*
* - a `scope` other than {@link OIDC_APP_TOKEN_SCOPE};
* - `nbf` other than `iat`, or `exp - iat` other than {@link OIDC_APP_TOKEN_TTL_SECONDS};
* - a `sub` that does not start with `org:<org slug>:app:<zion_app_slug>:`, where the org slug
*   passes the org host grammar of {@link oidcAppTokenSubject} (R-015). The payload carries no org slug claim, so the schema
*   checks the form; a relying party compares the whole `sub` with the one it expects;
* - an `iss` that is not {@link oidcAppIssuer} of an org issuer (issue at `iss`): no `/oidc`
*   suffix, a trailing `/`, or a nested suffix;
* - for a good `iss`, an `aud` that {@link isOidcAppAudienceAllowed} refuses for the org issuer
*   `iss` names (issue at `aud`): the org issuer or the org's own federated exchange URL;
* - an extra member whose name fails {@link isAssertableOidcAppClaimName} or whose value is not a
*   string of 1 to `OIDC_CLAIM_VALUE_MAX_LENGTH` characters; more than
*   {@link OIDC_APP_CLAIMS_MAX} extra members.
*/
const oidcAppTokenPayloadSchema = z.object({
	iss: oidcIssuerSchema,
	sub: z.string().min(1),
	aud: oidcAudienceSchema,
	scope: z.literal(OIDC_APP_TOKEN_SCOPE),
	iat: z.number().int(),
	nbf: z.number().int(),
	exp: z.number().int(),
	jti: z.string().min(1).max(256),
	zion_org_id: z.string().min(1),
	zion_app_slug: z.string().min(1),
	zion_fixture_id: z.string().min(1),
	zion_run_id: workRunIdSchema
}).catchall(z.string().min(1).max(256)).superRefine((payload, ctx) => {
	if (payload.nbf !== payload.iat || payload.exp - payload.iat !== 300) ctx.addIssue({
		code: "custom",
		path: ["exp"],
		message: `nbf equals iat and exp is iat plus 300`
	});
	const orgIssuer = orgIssuerOfOidcAppIssuer(payload.iss);
	if (orgIssuer === void 0) ctx.addIssue({
		code: "custom",
		path: ["iss"],
		message: "iss is the /oidc issuer of an org"
	});
	else if (!isOidcAppAudienceAllowed(payload.aud, orgIssuer)) ctx.addIssue({
		code: "custom",
		path: ["aud"],
		message: "aud names the org issuer or its federated exchange"
	});
	if (!isOidcAppTokenSubjectOf(payload.sub, payload.zion_app_slug)) ctx.addIssue({
		code: "custom",
		path: ["sub"],
		message: "sub starts with org:<org slug>:app:<zion_app_slug>:"
	});
	const extra = Object.keys(payload).filter((name) => !FIXED_PAYLOAD_MEMBERS.has(name));
	for (const name of extra) if (!isAssertableOidcAppClaimName(name)) ctx.addIssue({
		code: "custom",
		path: [name],
		message: "claim name: reserved"
	});
	if (extra.length > 16) ctx.addIssue({
		code: "custom",
		path: [],
		message: `at most 16 app claims`
	});
});
/**
* True when `value` is a string that {@link oidcAudienceSchema} admits; false for every other
* value, a non-string included. Never throws. Pure. The fixture rule `oidc-issue-audience-invalid`
* calls it, so the publish door, the CLI and the token route judge an audience with one predicate.
*
* The return is a plain `boolean` and no type guard (ruling R-001). A refused audience can still be
* a string, and a guard `value is string` narrows a `string` argument to `never` on the refusal
* branch, so the caller could not echo or log the value it refused. A caller that holds an
* `unknown` checks `typeof value === 'string'` first.
*/
function isOidcAudience(value) {
	return oidcAudienceSchema.safeParse(value).success;
}
/** `value` with one trailing `/` removed. */
function withoutTrailingSlash(value) {
	return value.endsWith("/") ? value.slice(0, -1) : value;
}
/**
* The org issuer an OIDC app token issuer names, or `undefined` when `issuer` is not
* {@link oidcAppIssuer} of any org issuer. `https://acme.zion.witness.ai/oidc` gives
* `https://acme.zion.witness.ai`. `undefined` for an issuer with no {@link OIDC_ISSUER_PATH}
* suffix, for `https://acme.zion.witness.ai/oidc/` and for `https://acme.zion.witness.ai/oidc/oidc`,
* because the part before the suffix is no org issuer or the issuer is not the one
* {@link oidcAppIssuer} prints. Never throws. Pure.
*/
function orgIssuerOfOidcAppIssuer(issuer) {
	if (!issuer.endsWith("/oidc")) return void 0;
	const orgIssuer = issuer.slice(0, -5);
	if (!isOrgIssuer(orgIssuer) || oidcAppIssuer(orgIssuer) !== issuer) return void 0;
	return orgIssuer;
}
/**
* True when an app of the org whose issuer is `orgIssuer` may ask an OIDC app token for `audience`
* (overview security table: no self-exchange). core-api's `POST /internal/v1/oidc-tokens` (WS-7)
* calls it after the `issue.audiences` membership check; {@link buildOidcAppTokenPayload} and
* {@link oidcAppTokenPayloadSchema} apply it again.
*
* The federated exchange of an org accepts an assertion whose `aud` is either of two values
* (core-api `federatedAssertionAudiences`, D-007): the org issuer, and the org issuer followed by
* `FEDERATED_TOKEN_PATH` (`/v1/auth/token/federated`). An OIDC app token with either audience could
* go back to the org's own exchange for a DP token, so this predicate refuses both.
*
* - `audience`: anything. False when it fails {@link isOidcAudience}.
* - `orgIssuer`: the org issuer, `https://<org host>`, with or without one trailing `/`. False when
*   it fails `oidcIssuerSchema` or carries a path (`https://acme.zion.witness.ai/oidc`), so an
*   unknown or nested org issuer refuses every audience.
* - The compare removes one trailing `/` from each side, so `https://acme.zion.witness.ai/` and
*   `https://acme.zion.witness.ai/v1/auth/token/federated/` are refused too. Byte equality
*   otherwise: `oidcAudienceSchema` admits only a lower-case host and no default port, so no other
*   spelling names the same URL.
*
* `isOidcAppAudienceAllowed('https://acme.zion.witness.ai/v1/auth/token/federated',
* 'https://acme.zion.witness.ai')` is false; the same audience for `https://other.zion.witness.ai`
* is true. The return is a plain `boolean` and no type guard: a refused audience is still a
* string, so a caller's refusal branch keeps its type (ruling R-001). Never throws. Pure.
*/
function isOidcAppAudienceAllowed(audience, orgIssuer) {
	if (typeof audience !== "string" || !isOidcAudience(audience)) return false;
	if (!isOrgIssuer(orgIssuer)) return false;
	const base = withoutTrailingSlash(orgIssuer);
	const candidate = withoutTrailingSlash(audience);
	return candidate !== base && candidate !== `${base}/v1/auth/token/federated`;
}
//#endregion
export { OIDC_ACCEPT_MAX_CLAIMS, OIDC_ACCEPT_MAX_ENTRIES, OIDC_ACCEPT_PATH_MAX_LENGTH, OIDC_ACCEPT_PATH_PATTERN, OIDC_APP_CLAIMS_MAX, OIDC_APP_PLATFORM_CLAIM_PREFIX, OIDC_APP_SUBJECT_MAX_LENGTH, OIDC_APP_TOKEN_MAX_LENGTH, OIDC_APP_TOKEN_PLATFORM_CLAIMS, OIDC_APP_TOKEN_RESERVED_CLAIMS, OIDC_APP_TOKEN_SCOPE, OIDC_APP_TOKEN_TTL_SECONDS, OIDC_AUDIENCE_MAX_LENGTH, OIDC_CAPABILITY_ID, OIDC_DISCOVERY_PATH, OIDC_HTTP_METHODS, OIDC_ISSUER_PATH, OIDC_ISSUE_MAX_AUDIENCES, OIDC_JWKS_PATH, OIDC_TOKENS_PATH, buildOidcAppTokenPayload, fixtureOidcAcceptEntrySchema, fixtureOidcCapabilitySchema, fixtureOidcIssueSchema, isAssertableOidcAppClaimName, isOidcAppAudienceAllowed, isOidcAudience, oidcAppClaimsSchema, oidcAppIssuer, oidcAppSubjectSchema, oidcAppTokenPayloadSchema, oidcAppTokenRequestSchema, oidcAppTokenResponseSchema, oidcAppTokenSubject, oidcAudienceSchema };

//# sourceMappingURL=oidc-app.js.map